import { GKButton } from "@granular/gds";
import { TRPCClientError } from "@trpc/react-query";
import React from "react";
import type { FallbackProps } from "react-error-boundary";
import { FormattedMessage } from "react-intl";
import { useTimeout } from "../hooks/useTimeout";
import { withIntlProvider } from "../helpers/I18n";
import translations from "../../i18n";
import Loading from "./Loading";
import BigCenterMessage from "./BigCenterMessage";

/**
 * Checks if an error is a token expiration error.
 * Token refresh is handled proactively in TrpcWrapper before requests are made.
 * This check is only used to show the appropriate error UI when refresh has already failed.
 */
const isTokenExpiredError = (error: unknown): boolean => {
  if (!(error instanceof TRPCClientError)) return false;

  const message = error.message?.toLowerCase() || "";
  const responseJSON = error.meta?.responseJSON as Record<string, unknown>;
  const exp = responseJSON?.exp;

  if (message.includes("token expired")) return true;
  if (typeof exp === "string" && exp.toLowerCase().includes("token expired"))
    return true;

  if ((error.data as Record<string, unknown>)?.code === "UNAUTHORIZED")
    return true;
  const responseStatus = (error.meta?.response as { status?: number })?.status;
  if (responseStatus === 401) return true;

  return false;
};

/**
 * Error fallback component to display when an error boundary is triggered
 * The error boundary is triggered when a fatal error occurs in a component that prevents us from displaying the actual content.
 * It's better to display an error state instead a blank page or empty data, which can lead the user to believe that their operation
 * is empty because it hasn't data and not because an error occurred.
 *
 * Important: Do not attempt to use hooks inside this component.
 *
 * When necessary you can raise a fatal error triggering the error boundary manually using:
 * @example
 * ```typescript
 * import { useErrorBoundary } from "react-error-boundary";
 *
 * const { showBoundary } = useErrorBoundary();
 *
 * try {
 *   // your code
 * } catch (error) {
 *   showBoundary(error); // firing the error boundary manually
 * }
 * ```
 */
export const Fallback: React.FC<FallbackProps> = ({
  resetErrorBoundary,
  error,
}) => {
  /**
   * Errors are sometimes a transient state... the app may be loading something
   * still. So wait a bit before showing.
   */
  const [waitedForLoading, setWaitedForLoading] = React.useState(false);
  useTimeout(() => setWaitedForLoading(true), 750);
  if (!waitedForLoading) {
    return <Loading />;
  }

  // Token refresh is handled proactively in TrpcWrapper. If we still get a
  // token-expired error here, the refresh has already failed — show sign-in UI.
  if (isTokenExpiredError(error)) {
    return (
      <BigCenterMessage
        color="danger"
        icon="Lock"
        heading={
          <span data-qa="errorBoundary-tokenExpired">
            <FormattedMessage id="Token_expired_error_heading" />
          </span>
        }
      >
        <p className="text-muted">
          <FormattedMessage id="Token_expired_error_description" />
        </p>
        <GKButton
          className="mt-3 w-100"
          color="light"
          data-qa="errorBoundary-tokenExpired-signin-btn"
          onClick={() => globalThis.location.assign("/logout")}
          outline
          size="lg"
        >
          <FormattedMessage id="Token_expired_sign_in_again" />
        </GKButton>
      </BigCenterMessage>
    );
  }

  if (
    error instanceof TRPCClientError &&
    (error?.meta?.response as { status?: number })?.status === 403 &&
    error?.message?.toLowerCase()?.includes("super-user")
  ) {
    return (
      <BigCenterMessage
        color="danger"
        icon="Lock"
        heading={
          <span data-qa="container-errorBoundary-unauthorized">
            Super-users and super-readers are not supported.
          </span>
        }
      >
        <GKButton
          className="mt-3 w-100"
          color="light"
          data-qa="container-errorBoundary-logout-btn"
          onClick={() => globalThis.location.assign("/logout")}
          outline
          size="lg"
        >
          Log out
        </GKButton>
      </BigCenterMessage>
    );
  }

  return (
    <BigCenterMessage
      color="danger"
      heading={
        <span data-qa="errorBoundary-UnableToLoadPage">
          <FormattedMessage id="Unable_to_load_the_page" />.
        </span>
      }
    >
      <GKButton
        className="mt-3 w-100"
        color="light"
        data-qa="errorBoundary-reset-btn"
        onClick={() => resetErrorBoundary()}
        outline
        size="lg"
      >
        <FormattedMessage id="common_refresh" />
      </GKButton>
    </BigCenterMessage>
  );
};

const PolyglotFallback = withIntlProvider(Fallback, { translations });

export default PolyglotFallback;
